import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Box, CircularProgress, CssBaseline } from '@mui/material';
import { ThemeProvider } from './features/shared/context/ThemeContext';
import { AuthProvider, useAuth } from './features/shared/context/AuthContext';
import { PermissionProvider } from './features/shared/context/PermissionContext';
import { ConsultoriaIdentityBridge } from './features/consultoria';
import { Layout } from './features/shared/components/Layout';
import { ToastProvider } from './features/shared/components/SuccessToast';
import { FilterHeaderMismatchProvider } from './features/shared/components/DynamicFilters';
import SplashScreen from './components/SplashScreen';
import { TabManagerProvider, useTabManager } from './features/shared/context/TabManagerContext';
import { AppReadyProvider, useAppReady } from './features/shared/context/AppReadyContext';
import { NotFoundPage } from './features/shared/components/NotFoundPage';
import { usePermissions } from './features/shared/context/PermissionContext';
import { useAccessControl } from './hooks/useAccessControl';
import { FALLBACK_ACCESSIBLE_PATH, resolveDefaultAccessiblePath } from './features/shared/utils/getDefaultAccessiblePath';
import { ChunkLoadErrorBoundary } from './features/shared/components/ChunkLoadErrorBoundary';
import { consumeChunkRecoverySkipSplash } from './features/shared/utils/chunkRecovery';
import { FloatingTrainingVideoProvider } from './features/manual/context/FloatingTrainingVideoContext';
import { FloatingTrainingVideoPlayer } from './features/manual/components/FloatingTrainingVideoPlayer';
import {
  createProtectedRouteElements,
  initializeProtectedRouteRegistry,
  TabKeepAliveOutlet,
} from './features/shared/routing';

// ── Auth (public) ─────────────────────────────────────────────────────────────
const LoginPage = React.lazy(() =>
  import('./features/auth/page/LoginPage').then((m) => ({ default: m.LoginPage }))
);
const SignupPage = React.lazy(() =>
  import('./features/auth/page/SignupPage').then((m) => ({ default: m.SignupPage }))
);
const PerfilPage = React.lazy(() =>
  import('./features/auth/page/perfilpage').then((m) => ({ default: m.PerfilPage }))
);

// ── Dashboard ─────────────────────────────────────────────────────────────────

// ── Production — planning ─────────────────────────────────────────────────────
const ProductionPlanningPage = React.lazy(() =>
  import('./features/production/page/ProductionPlanningPage')
);
const OrdensProducaoPage = React.lazy(() =>
  import('./features/production/planning/page/OrdensProducaoPage').then((m) => ({ default: m.OrdensProducaoPage }))
);
const OperacoesPage = React.lazy(() =>
  import('./features/production/planning/processos/page/OperacoesPage').then((m) => ({ default: m.OperacoesPage }))
);
const SequenciamentoPage = React.lazy(() =>
  import('./features/production/planning/page/SequenciamentoPage')
);
// ── Production — control ──────────────────────────────────────────────────────
const ProductionControlPage = React.lazy(() =>
  import('./features/production/page/ProductionControlPage')
);
const DashboardsGeraisPage = React.lazy(() =>
  import('./features/production/control/dashboardgeral/page/DashboardsGeraisPage')
);
const MelhoriaPage = React.lazy(() =>
  import('./features/production/control/melhoria/page/MelhoriaPage')
);
const AuditoriaPage = React.lazy(() =>
  import('./features/production/control/auditoria/page/AuditoriaPage').then((m) => ({ default: m.AuditoriaPage }))
);
const RelatoriosPage = React.lazy(() =>
  import('./features/production/control/relatorios/page/RelatoriosPage').then((m) => ({ default: m.RelatoriosPage }))
);
const MonitoramentoPage = React.lazy(() =>
  import('./features/production/control/monitoramento/page/MonitoramentoPage').then((m) => ({
    default: m.MonitoramentoPage,
  }))
);
const NaoConformidadesPage = React.lazy(() =>
  import('./features/production/control/naoconformidades/page/NaoConformidadesPage').then((m) => ({
    default: m.NaoConformidadesPage,
  }))
);
const LotesPage = React.lazy(() =>
  import('./features/production/control/lotes/page/LotesPage').then((m) => ({
    default: m.LotesPage,
  }))
);
// Factory 3D module (reset on main; lazy chunk isolated from other routes)
const FactoryViewPage = React.lazy(() => import('./features/factory-3d'));

// ── Production — apontamento (~1730 lines) — isolated chunk ───────────────────
const ApontamentoPage = React.lazy(() =>
  import('./features/production/apontamento/page/ApontamentoPage').then((m) => ({ default: m.ApontamentoPage }))
);

// ── Production — parametrizacao ───────────────────────────────────────────────
const ParametrizacaoPage = React.lazy(() =>
  import('./features/production/control/parametrizacao/page/ParametrizacaoPage').then((m) => ({ default: m.ParametrizacaoPage }))
);
const SetoresPage = React.lazy(() =>
  import('./features/production/control/parametrizacao/setores/page/SetoresPage').then((m) => ({ default: m.SetoresPage }))
);
const MaquinasPage = React.lazy(() =>
  import('./features/production/control/parametrizacao/maquinas/page/MaquinasPage').then((m) => ({ default: m.MaquinasPage }))
);
const CategoriasPage = React.lazy(() =>
  import('./features/production/control/parametrizacao/categorias/page/CategoriasPage').then((m) => ({ default: m.CategoriasPage }))
);
const CausasPage = React.lazy(() =>
  import('./features/production/control/parametrizacao/causas/page/CausasPage').then((m) => ({ default: m.CausasPage }))
);
const HorariosPage = React.lazy(() =>
  import('./features/production/control/parametrizacao/horarios/page/HorariosPage').then((m) => ({ default: m.HorariosPage }))
);

// ── Maintenance ───────────────────────────────────────────────────────────────
const MaintenanceControlPage = React.lazy(() =>
  import('./features/maintenance/control/page/MaintenanceControlPage')
);
const MaintenancePlanningPage = React.lazy(() =>
  import('./features/maintenance/planning/page/MaintenancePlanningPage')
);
const MaintenanceDashboardPage = React.lazy(() =>
  import('./features/maintenance/dashboards/page/MaintenanceDashboardPage')
);
const MaintenanceSensoresPage = React.lazy(() =>
  import('./features/maintenance/parametrizacao/sensores/page/SensoresPage')
);
const MachineDetailPage = React.lazy(() =>
  import('./features/maintenance/control/page/MachineDetailPage')
);
const MaintenanceControlPlaceholderPage = React.lazy(() =>
  import('./features/maintenance/control/page/MaintenanceControlPlaceholderPage')
);

// ── Sensoriamento ─────────────────────────────────────────────────────────────
const SensoresSensoriamentoPage = React.lazy(() =>
  import('./features/sensoriamento/page/SensoresPage')
);
const RelatoriosSensoriamentoPage = React.lazy(() =>
  import('./features/sensoriamento/page/RelatoriosPage')
);
const AtivacaoDispositivoPage = React.lazy(() =>
  import('./features/sensoriamento/page/AtivacaoDispositivoPage')
);

// ── Product Engineering ───────────────────────────────────────────────────────
const EngenhariaPage = React.lazy(() =>
  import('./features/product_engineering/page/EngenhariaPage')
);
const EngProdutoPage = React.lazy(() =>
  import('./features/product_engineering/page/EngProdutoPage')
);
const MontagemPage = React.lazy(() =>
  import('./features/product_engineering/page/MontagemPage')
);
const ComponentesPage = React.lazy(() =>
  import('./features/product_engineering/page/ComponentesPage')
);
const ProcessosPage = React.lazy(() =>
  import('./features/product_engineering/page/ProcessosPage')
);

// ── Cadastros ─────────────────────────────────────────────────────────────────
const CadastrosPage = React.lazy(() =>
  import('./features/cadastros/page/CadastrosPage').then((m) => ({ default: m.CadastrosPage }))
);
const ItensConsumoPage = React.lazy(() =>
  import('./features/cadastros/page/ItensConsumoPage').then((m) => ({ default: m.ItensConsumoPage }))
);
const ClientesPage = React.lazy(() =>
  import('./features/cadastros/page/ClientesPage').then((m) => ({ default: m.ClientesPage }))
);
const FormasPagamentoPage = React.lazy(() =>
  import('./features/cadastros/page/FormasPagamentoPage').then((m) => ({ default: m.FormasPagamentoPage }))
);

// ── Admin ─────────────────────────────────────────────────────────────────────
const AdminPanel = React.lazy(() => import('./features/settings/page/AdminPanel'));

// ── Quality ───────────────────────────────────────────────────────────────────
const ParametrosQualidadePage = React.lazy(() =>
  import('./features/quality/page/ParametrosQualidadePage').then((m) => ({ default: m.ParametrosQualidadePage }))
);
const ChecklistPage = React.lazy(() =>
  import('./features/quality/page/ChecklistPage').then((m) => ({ default: m.ChecklistPage }))
);
const RegrasQualidadePage = React.lazy(() =>
  import('./features/quality/page/RegrasQualidadePage').then((m) => ({ default: m.RegrasQualidadePage }))
);
const AuditoriaQualidadePage = React.lazy(() =>
  import('./features/quality/page/AuditoriaQualidadePage').then((m) => ({ default: m.AuditoriaQualidadePage }))
);
const DashboardsQualidadePage = React.lazy(() =>
  import('./features/quality/page/DashboardsQualidadePage').then((m) => ({ default: m.DashboardsQualidadePage }))
);
const PlanejamentoChecklistPage = React.lazy(() =>
  import('./features/quality/page/PlanejamentoChecklistPage').then((m) => ({ default: m.PlanejamentoChecklistPage }))
);
const OperacaoQualidadePage = React.lazy(() =>
  import('./features/quality/page/OperacaoQualidadePage').then((m) => ({ default: m.OperacaoQualidadePage }))
);

// ── Suprimentos ───────────────────────────────────────────────────────────────
const SuprimentosPage = React.lazy(() =>
  import('./features/suprimentos/page/SuprimentosPage').then((m) => ({ default: m.SuprimentosPage }))
);
const SuprimentosEstoquePage = React.lazy(() =>
  import('./features/suprimentos/page/SuprimentosEstoquePage').then((m) => ({ default: m.SuprimentosEstoquePage }))
);
const SuprimentosSeparacoesPage = React.lazy(() =>
  import('./features/suprimentos/page/SuprimentosSeparacoesPage').then((m) => ({ default: m.SuprimentosSeparacoesPage }))
);
const SuprimentosMovimentacoesPage = React.lazy(() =>
  import('./features/suprimentos/page/SuprimentosMovimentacoesPage').then((m) => ({ default: m.SuprimentosMovimentacoesPage }))
);
const SuprimentosParametrizacaoPage = React.lazy(() =>
  import('./features/suprimentos/page/SuprimentosParametrizacaoPage').then((m) => ({ default: m.SuprimentosParametrizacaoPage }))
);
const SuprimentosNotasFiscaisPage = React.lazy(() =>
  import('./features/suprimentos/page/SuprimentosNotasFiscaisPage').then((m) => ({ default: m.SuprimentosNotasFiscaisPage }))
);

// ── Sales ─────────────────────────────────────────────────────────────────────
const SalesPdvPage = React.lazy(() =>
  import('./features/sales/page/SalesPdvPage').then((m) => ({ default: m.SalesPdvPage }))
);
const SalesPedidosPage = React.lazy(() =>
  import('./features/sales/page/SalesPedidosPage').then((m) => ({ default: m.SalesPedidosPage }))
);
const SalesTabelaPage = React.lazy(() =>
  import('./features/sales/page/SalesTabelaPage').then((m) => ({ default: m.SalesTabelaPage }))
);

// ── Settings / Manual ─────────────────────────────────────────────────────────
const SettingsPage = React.lazy(() => import('./features/settings/page/SettingsPage'));
const ManualPage = React.lazy(() =>
  import('./features/manual/page/ManualPage').then((m) => ({ default: m.ManualPage }))
);

initializeProtectedRouteRegistry({
  PerfilPage,
  SensoresSensoriamentoPage,
  RelatoriosSensoriamentoPage,
  AtivacaoDispositivoPage,
  EngenhariaPage,
  EngProdutoPage,
  ComponentesPage,
  ProcessosPage,
  MontagemPage,
  ProductionPlanningPage,
  SequenciamentoPage,
  OrdensProducaoPage,
  OperacoesPage,
  ProductionControlPage,
  AuditoriaPage,
  NaoConformidadesPage,
  LotesPage,
  RelatoriosPage,
  ApontamentoPage,
  MonitoramentoPage,
  DashboardsGeraisPage,
  MelhoriaPage,
  FactoryViewPage,
  ParametrizacaoPage,
  MachineDetailPage,
  MaintenanceControlPlaceholderPage,
  MaintenanceControlPage,
  MaintenancePlanningPage,
  MaintenanceDashboardPage,
  MaintenanceSensoresPage,
  CadastrosPage,
  ItensConsumoPage,
  ClientesPage,
  FormasPagamentoPage,
  SetoresPage,
  MaquinasPage,
  CategoriasPage,
  HorariosPage,
  CausasPage,
  SuprimentosPage,
  SuprimentosEstoquePage,
  SuprimentosSeparacoesPage,
  SuprimentosMovimentacoesPage,
  SuprimentosParametrizacaoPage,
  SuprimentosNotasFiscaisPage,
  SalesPdvPage,
  SalesPedidosPage,
  SalesTabelaPage,
  ParametrosQualidadePage,
  ChecklistPage,
  RegrasQualidadePage,
  AuditoriaQualidadePage,
  DashboardsQualidadePage,
  PlanejamentoChecklistPage,
  OperacaoQualidadePage,
  SettingsPage,
  ManualPage,
  NotFoundPage,
  AdminPanel,
});

// ── Loading fallback shown while a lazy chunk is fetching ─────────────────────
/** Marked so splash boot logic does not treat route chunk loading as “app not ready”. */
const PageLoadingFallback: React.FC = () => (
  <Box
    data-splash-exclude
    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}
  >
    <CircularProgress size={32} aria-valuenow={0} aria-valuemin={0} aria-valuemax={100} />
  </Box>
);

// Componente para rotas protegidas
const ProtectedRoute: React.FC<{ children: React.ReactNode; requireSuperAdmin?: boolean }> = ({ children, requireSuperAdmin }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { hasPermission } = usePermissions();
  const { isSuperAdmin, loading: accessLoading } = useAccessControl();
  const defaultAccessiblePath = React.useMemo(
    () => resolveDefaultAccessiblePath(hasPermission, isSuperAdmin),
    [hasPermission, isSuperAdmin]
  );
  const forbiddenRedirect =
    defaultAccessiblePath !== '/admin-panel' ? defaultAccessiblePath : FALLBACK_ACCESSIBLE_PATH;

  // O splash global já cobre o carregamento inicial; evitar abrir outro splash aqui.
  if (isLoading || (requireSuperAdmin && accessLoading)) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireSuperAdmin && !isSuperAdmin) {
    return <Navigate to={forbiddenRedirect} replace />;
  }

  return <>{children}</>;
};


// Componente para layout protegido que não desmonta ao trocar de rota
const ProtectedLayout: React.FC<{ requireSuperAdmin?: boolean }> = ({ requireSuperAdmin }) => {
  return (
    <ProtectedRoute requireSuperAdmin={requireSuperAdmin}>
      <FloatingTrainingVideoProvider>
        <Layout>
          <TabKeepAliveOutlet notFoundElement={<NotFoundPage />} />
          <Outlet />
        </Layout>
        <FloatingTrainingVideoPlayer />
      </FloatingTrainingVideoProvider>
    </ProtectedRoute>
  );
};

// Componente principal da aplicação
const AppContent: React.FC = () => {
  return (
    <Router>
      <TabManagerProvider>
        <AppReadyProvider>
          <AppWithTabs />
        </AppReadyProvider>
      </TabManagerProvider>
    </Router>
  );
};

// Componente interno que precisa do TabManager
export const AppWithTabs: React.FC = () => {
  const skipBootSplashRef = React.useRef(consumeChunkRecoverySkipSplash());
  const lastBootUserIdRef = React.useRef<string | null>(null);
  const hasCompletedInitialBootRef = React.useRef(false);
  const { isBootstrapping, preferredInitialPath } = useTabManager();
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const { isSuperAdmin } = useAccessControl();
  const { isAppReady, setAppReady } = useAppReady();
  const [splashPhase, setSplashPhase] = React.useState<'show' | 'fading' | 'hidden'>('hidden');
  const [bootForcedRecover, setBootForcedRecover] = React.useState(false);

  const BOOT_WATCHDOG_MS = 12_000;
  const isBootBlocked = isAuthenticated && (permissionsLoading || isBootstrapping);

  const bootComplete = isAuthenticated && !authLoading && !permissionsLoading && !isBootstrapping;

  if (user?.id !== lastBootUserIdRef.current) {
    lastBootUserIdRef.current = user?.id ?? null;
    hasCompletedInitialBootRef.current = false;
  }
  if (bootComplete || bootForcedRecover) {
    hasCompletedInitialBootRef.current = true;
  }

  // Favoritos/permissões travados: libera rotas após timeout.
  React.useEffect(() => {
    if (!isBootBlocked) {
      setBootForcedRecover(false);
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setBootForcedRecover(true);
      setAppReady(true);
    }, BOOT_WATCHDOG_MS);
    return () => window.clearTimeout(timeoutId);
  }, [isBootBlocked, setAppReady]);

  React.useEffect(() => {
    if (!isAuthenticated) {
      setSplashPhase('hidden');
      return;
    }
    if (skipBootSplashRef.current) {
      setSplashPhase('hidden');
      return;
    }
    setSplashPhase((prev) => (prev === 'hidden' ? 'show' : prev));
  }, [isAuthenticated, user?.id]);

  // Fecha o splash quando o boot terminou e o Layout montou (sem heurística de DOM).
  // O watchdog também fecha: ele libera as rotas, e o splash por cima as esconderia
  // enquanto as permissões seguissem pendentes.
  React.useEffect(() => {
    if (splashPhase !== 'show') return;
    if (!bootForcedRecover && (!bootComplete || !isAppReady)) return;
    setSplashPhase('fading');
  }, [bootComplete, isAppReady, bootForcedRecover, splashPhase]);

  const handleFadeOutComplete = React.useCallback(() => {
    setSplashPhase('hidden');
  }, []);

  const showSplash = isAuthenticated && splashPhase !== 'hidden';

  const splashOverlay = showSplash ? (
    <SplashScreen
      message="Carregando sistema..."
      visible={splashPhase === 'show'}
      onFadeOutComplete={handleFadeOutComplete}
    />
  ) : null;

  const defaultAccessiblePath = React.useMemo(
    () => resolveDefaultAccessiblePath(hasPermission, isSuperAdmin),
    [hasPermission, isSuperAdmin]
  );
  const getInitialPath = (): string => preferredInitialPath || defaultAccessiblePath;

  // Aguarda favoritos/permissões só no primeiro boot. Depois disso, um flicker de
  // permissionsLoading (ex.: revalidação no foco da janela) não pode desmontar as rotas.
  const shouldBlockRoutesForBoot =
    isBootBlocked &&
    !bootForcedRecover &&
    !skipBootSplashRef.current &&
    !hasCompletedInitialBootRef.current;

  if (shouldBlockRoutesForBoot) {
    return <>{splashOverlay}</>;
  }

  return (
    <>
      <ChunkLoadErrorBoundary suspenseFallback={<PageLoadingFallback />}>
          <Routes>
          {/* Rotas públicas */}
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to={getInitialPath()} replace /> : <LoginPage />}
          />
          <Route
            path="/signup"
            element={isAuthenticated ? <Navigate to={getInitialPath()} replace /> : <SignupPage />}
          />

          {/* Rotas protegidas */}
          <Route element={<ProtectedLayout />}>
            {createProtectedRouteElements({
              getInitialPath,
              notFoundElement: <NotFoundPage />,
              excludePaths: ['/admin-panel'],
            })}
          </Route>

          {/* Painel de Administração Multi-tenant */}
          <Route element={<ProtectedLayout requireSuperAdmin />}>
            {createProtectedRouteElements({
              getInitialPath,
              notFoundElement: <NotFoundPage />,
              onlyPaths: ['/admin-panel'],
              includeFallbackRoutes: false,
            })}
          </Route>
          </Routes>
      </ChunkLoadErrorBoundary>
      {splashOverlay}
    </>
  );
};

// App principal
const App: React.FC = () => {
  return (
    <ThemeProvider>
      <CssBaseline />
      <AuthProvider>
        <PermissionProvider>
          <ConsultoriaIdentityBridge>
            <ToastProvider>
              <FilterHeaderMismatchProvider>
                <AppContent />
              </FilterHeaderMismatchProvider>
            </ToastProvider>
          </ConsultoriaIdentityBridge>
        </PermissionProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
